import { createAsyncThunk } from '@reduxjs/toolkit';
import { setLastRequestAmended } from 'core/visitRequestForm/visitRequestFormSlice';
import { fetchApiAuth } from 'core/utils/api';
import { notification } from 'antd';
import { handleError } from 'core/utils/helpers';
import { UploadFile } from 'antd/lib/upload/interface';
import { VisitRequestFormResponse, AmendActionResponse } from './types';

export const createGuardingRequest = createAsyncThunk(
    'guardingRequestForm/create',
    async (payload: { siteId: number }, { rejectWithValue }) => {
        try {
            const response: VisitRequestFormResponse = await fetchApiAuth({
                method: 'POST',
                url: 'guarding-request/create',
                body: { siteId: payload.siteId },
            });

            notification.success({
                message: 'Success',
                description: 'Guarding request created successfully',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const deleteGuardingRequest = createAsyncThunk(
    'guardingRequestForm/delete',
    async (id: string | number, { rejectWithValue }) => {
        try {
            const response = await fetchApiAuth({
                method: 'DELETE',
                url: `guarding-request/${id}`,
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            if (errorMessage !== false) {
                console.error('Failed to delete draft guarding request:', errorMessage);
                return rejectWithValue(errorMessage);
            }
            throw error;
        }
    },
);

export const loadGuardingRequestForm = createAsyncThunk(
    'guardingRequestForm/load',
    async (payload: { id: string | number, step: number, amending?: boolean }, { rejectWithValue }) => {
        try {
            const { id, step, amending = false } = payload;
            const response: VisitRequestFormResponse = await fetchApiAuth({
                method: 'GET',
                url: `guarding-request/${id}/step/${step}?amending=${amending}`,
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const updateGuardingRequestForm = createAsyncThunk(
    'guardingRequestForm/update',
    async (payload: { id: string | number; step: number; data: Record<string, unknown> }, { rejectWithValue }) => {
        const { id, data } = payload;
        try {
            const response: VisitRequestFormResponse = await fetchApiAuth({
                method: 'PUT',
                url: `guarding-request/${id}`,
                body: { ...data },
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const uploadGuardingRequestAttachment = createAsyncThunk(
    'guardingRequestForm/uploadAttachment',
    async (payload: { id: string, attachment: UploadFile, step?: string }, { rejectWithValue }) => {
        const { id, attachment, step } = payload;
        const formData = new FormData();
        try {
            const file = attachment.originFileObj;
            if (file) {
                formData.append('file', file);
            }
            if (step) {
                formData.append('step', step);
            }
            const response = await fetchApiAuth({
                method: 'POST',
                url: `guarding-request/${id}/upload-attachment`,
                body: formData,
            });

            notification.success({
                message: 'Success',
                description: 'File uploaded successfully.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const deleteGuardingRequestAttachment = createAsyncThunk(
    'guardingRequestForm/deleteAttachment',
    async (payload: { id: string, attachmentId: number }, { rejectWithValue }) => {
        const { id, attachmentId } = payload;
        try {
            const response = await fetchApiAuth({
                method: 'DELETE',
                url: `guarding-request/${id}/delete-attachment/${attachmentId}`,
            });

            notification.success({
                message: 'Success',
                description: 'File successfully removed.',
            });

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);

export const amendGuardingRequestAction = createAsyncThunk(
    'guardingRequestForm/amend',
    async (payload: { id: string | number, sections: string[], details: string }, { rejectWithValue, dispatch }) => {
        const { id, sections, details } = payload;

        try {
            const response: AmendActionResponse = await fetchApiAuth({
                method: 'POST',
                url: `guarding-request/${id}/action`,
                body: {
                    action: 'amend',
                    sections,
                    details,
                },
            });

            notification.success({
                message: 'Success',
                description: response.data.message,
            });

            // The amendment is committed, so there is nothing pending to clean
            // up. Leaving the marker set made the next dashboard / request
            // view visit cancel it and report "amendment has been cancelled".
            dispatch(setLastRequestAmended(null));

            return response.data;
        } catch (error: unknown) {
            const errorMessage = handleError(error);
            const msg = errorMessage || 'An unexpected error occurred. Please try again.';
            notification.error({
                message: 'Error',
                description: msg,
            });
            return rejectWithValue(msg);
        }
    },
);
